/**
 * Tipos de tipos de ativos
 */
export enum Status {
  OPEN = "OPEN",
  COMPLETED = "COMPLETED",
  CLOSED = "CLOSED",
}

export interface FilledFormOutputDTO {
  _id?: string;
  phaseForm?: string;
  processId?: string;
  clientId?: string;
  data?: any;
  createdAt?: Date;
  updatedAt?: Date;
  status?: Status;
  userEmail?: string;
  openedAt?: Date;
  downloadedAt?: Date;
  hasDownload?: boolean;
  appointmentSchedule?: {
    status: string;
    date: string;
    clientName?: string;
    clientEmail?: string;
    clientPhone?: string;
    notes?: string;
  };
}

export const StatusLabels: Record<Status, string> = {
  [Status.OPEN]: "Aberto",
  [Status.COMPLETED]: "Preenchido",
  [Status.CLOSED]: "Fechado",
};

/**
 * Estado visual derivado: o que o advogado vê na listagem de filledForms recebidos.
 * Diferente do `status` (que reflete o ciclo de vida do preenchimento pelo cliente):
 * aqui medimos o ENGAJAMENTO do advogado com o filledForm.
 */
export enum FilledFormViewState {
  AGUARDANDO = "AGUARDANDO", // chegou, advogado nunca abriu
  ABERTO = "ABERTO",         // advogado abriu mas (se tem download) ainda não baixou
  BAIXADO = "BAIXADO",       // advogado baixou o anexo (só pra forms com download)
}

export const ViewStateLabels: Record<FilledFormViewState, string> = {
  [FilledFormViewState.AGUARDANDO]: "Aguardando",
  [FilledFormViewState.ABERTO]: "Aberto",
  [FilledFormViewState.BAIXADO]: "Baixado",
};

/**
 * Resolve o estado visual do filledForm para o advogado.
 * Regras (transcrição da reunião):
 * - Sem openedAt → AGUARDANDO
 * - Com openedAt + sem hasDownload → ABERTO (estado final, missão cumprida)
 * - Com openedAt + hasDownload + sem downloadedAt → ABERTO (missão NÃO cumprida)
 * - Com downloadedAt → BAIXADO (missão cumprida)
 */
export function getFilledFormViewState(
  form: Pick<FilledFormOutputDTO, "openedAt" | "downloadedAt" | "hasDownload">
): FilledFormViewState {
  if (form.downloadedAt) return FilledFormViewState.BAIXADO;
  if (form.openedAt) return FilledFormViewState.ABERTO;
  return FilledFormViewState.AGUARDANDO;
}

/**
 * Indica se a "missão" do advogado foi cumprida:
 * - Forms com download: cumpriu quando baixou.
 * - Forms sem download: cumpriu quando só abriu.
 */
export function isFilledFormMissionComplete(
  form: Pick<FilledFormOutputDTO, "openedAt" | "downloadedAt" | "hasDownload">
): boolean {
  if (form.hasDownload) return !!form.downloadedAt;
  return !!form.openedAt;
}

export interface FilledFormData {
  phaseForm: string;
  processId: string;
  clientId?: string;
  data: Array<{ field: any; data: any }>;
  status: Status;
}

export interface TokenValidationResponse {
  valid: boolean;
  message?: string;
  /** Enviado pelo backend junto de `valid:false` para separar token errado de token vencido. */
  reason?: "INVALID" | "EXPIRED";
}

export interface TokenResendResponse {
  success: boolean;
  message?: string;
}
